// src/components/foam/FoamVientoChat.js
//
// Native Foam-styled Viento chat (Nigel, 2026-06-05) — replaces the iframed
// viento.waka.surf widget inside my.waka.surf. No bounding box, no widget
// chrome: messages flow in the page, styled by the WAKA design system.
//
// Talks directly to the same backend as the widget:
//   POST https://us-central1-waka-agent.cloudfunctions.net/chat  (cors: true)
//   body { messages:[{role,content}], conversationId, location }
//   resp { message, conversationId, escalated?, escalationMessage?, error? }
//
// Props:
//   seed      — optional user message auto-sent on mount (e.g. the found-board
//               handoff). Rendered in-thread like any user message.
//   greeting  — optional local assistant bubble shown before any API call
//               (the old widget's "Hey there!" was client-side too).

import React, { useState, useRef, useEffect, useCallback } from 'react';

const CHAT_API = 'https://us-central1-waka-agent.cloudfunctions.net/chat';

// Escape first, then apply Viento's markdown-lite (bold / links / newlines) —
// same formatting the widget supported, minus the XSS.
function renderViento(content) {
  const escaped = String(content)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\[(.*?)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" style="text-decoration:underline;color:inherit;">$1</a>')
    .replace(/\n/g, '<br/>');
}

const Bubble = ({ role, children, html }) => (
  <div
    className="text-[14px] leading-[1.5] p-[14px] max-w-[85%]"
    style={{
      alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
      background: role === 'user' ? 'var(--foam-carbon)' : 'var(--foam-paper-2)',
      color: role === 'user' ? 'var(--foam-paper)' : 'var(--foam-ink)',
      borderRadius: 0,
    }}
    {...(html ? { dangerouslySetInnerHTML: { __html: html } } : {})}
  >
    {html ? undefined : children}
  </div>
);

const FoamVientoChat = ({ seed = null, greeting = null, placeholder = 'Type your message…' }) => {
  const [messages, setMessages] = useState([]); // {role, content}
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [escalation, setEscalation] = useState(null);
  const [error, setError] = useState(false);
  const endRef = useRef(null);
  const seededRef = useRef(false);

  const send = useCallback(async (text, history) => {
    const userMsg = { role: 'user', content: text };
    const newHistory = [...history, userMsg];
    setMessages(newHistory);
    setSending(true);
    setError(false);
    try {
      const res = await fetch(CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newHistory, conversationId, location: null }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setConversationId(data.conversationId || conversationId);
      setMessages([...newHistory, { role: 'assistant', content: data.message }]);
      if (data.escalated) setEscalation(data.escalationMessage || 'Our team will reach out shortly.');
    } catch (e) {
      console.error('Viento chat error:', e);
      setError(true);
    } finally {
      setSending(false);
    }
  }, [conversationId]);

  // Auto-send the seed once (found-board handoff)
  useEffect(() => {
    if (seed && !seededRef.current) {
      seededRef.current = true;
      send(seed, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, sending]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    send(text, messages);
  };

  return (
    <div className="flex flex-col gap-[10px]">
      <div className="flex flex-col gap-[10px]" style={{ minHeight: 120 }}>
        {greeting && messages.length === 0 && !sending && (
          <Bubble role="assistant">{greeting}</Bubble>
        )}
        {messages.map((m, i) =>
          m.role === 'assistant'
            ? <Bubble key={i} role="assistant" html={renderViento(m.content)} />
            : <Bubble key={i} role="user">{m.content}</Bubble>
        )}
        {sending && (
          <div className="font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2">
            ● Viento is typing…
          </div>
        )}
        {escalation && (
          <div
            className="p-[12px] text-[13px] leading-[1.5]"
            style={{ border: '1.5px solid var(--foam-orange)', color: 'var(--foam-ink)' }}
          >
            <span className="font-mono text-[12px] tracking-[0.1em] uppercase" style={{ color: 'var(--foam-orange-d)' }}>
              ✓ Support ticket created
            </span>
            <br />{escalation}
          </div>
        )}
        {error && (
          <div className="font-mono text-[12px] tracking-[0.08em] uppercase text-foam-orange-d">
            ● Connection hiccup — try again, or email info@waka.surf
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex gap-[8px]">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          maxLength={1000}
          className="
            flex-1 font-sans text-[15px] px-[14px]
            bg-foam-paper-2 text-foam-ink placeholder:text-foam-mute
            focus:outline-none focus:ring-2 focus:ring-foam-ink border-0
          "
          style={{ borderRadius: 0, height: 52 }}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          aria-label="Send"
          className="
            flex items-center justify-center flex-shrink-0
            bg-foam-orange text-foam-carbon font-sans font-bold text-[18px]
            disabled:opacity-50 disabled:cursor-not-allowed
            active:scale-[0.99] transition-transform
          "
          style={{ borderRadius: 0, width: 52, height: 52 }}
        >
          →
        </button>
      </form>
    </div>
  );
};

export default FoamVientoChat;
